/**
 * Comparison data shared between the /compare hub page and the per-duo
 * /compare/<a>-vs-<b> pages.
 *
 * Most cell values are brand names, tech terms, or short phrases. To keep
 * the data readable, we leave language-neutral values inline (Splunk, GCP,
 * <200ms, $15,000/mo) and only mark phrases that need translation with an
 * i18n-style key (e.g. `t:flat-rate-plans`). The runtime resolves these keys
 * via the locale provider and falls back to the literal value if the locale
 * doesn't define the key.
 */

import type { ComponentType } from 'react';
import { DollarSign, Search, Wrench, Zap, Server, Clock, Database, Shield, Rocket } from 'lucide-react';

export type CompetitorId = 'splunk' | 'datadog' | 'elk';

export interface Competitor {
  id: CompetitorId;
  name: string;
}

export const COMPETITORS: Record<CompetitorId, Competitor> = {
  splunk: { id: 'splunk', name: 'Splunk' },
  datadog: { id: 'datadog', name: 'Datadog' },
  elk: { id: 'elk', name: 'ELK Stack' },
};

export type CellValue = string | boolean;

export interface ComparisonRow {
  /** i18n key under `compare.rows.*` for the row label. */
  featureKey: string;
  logpulse: CellValue;
  splunk: CellValue;
  datadog: CellValue;
  elk: CellValue;
}

export const comparisonRows: ComparisonRow[] = [
  {
    featureKey: 'pricingModel',
    logpulse: 't:flat-rate',
    splunk: 't:per-gb-indexed',
    datadog: 't:per-host-gb',
    elk: 't:free-infra',
  },
  {
    featureKey: 'startingPrice',
    logpulse: 't:starting-logpulse',
    splunk: '~$15,000/mo',
    datadog: '~$8,000/mo',
    elk: '$3,000-5,000/mo',
  },
  {
    featureKey: 'searchSpeed',
    logpulse: '<200ms',
    splunk: '<500ms',
    datadog: '<1s',
    elk: '1-5s',
  },
  {
    featureKey: 'queryLanguage',
    logpulse: 'LPQL',
    splunk: 'SPL',
    datadog: 't:custom',
    elk: 'Lucene / KQL',
  },
  {
    featureKey: 'aiAnomaly',
    logpulse: 't:built-in',
    splunk: 't:add-on-itsi',
    datadog: 't:built-in',
    elk: 't:xpack-paid',
  },
  {
    featureKey: 'aiInvestigator',
    logpulse: 't:all-plans',
    splunk: false,
    datadog: 't:datadog-bits-enterprise',
    elk: false,
  },
  {
    featureKey: 'visualPipelines',
    logpulse: 't:built-in',
    splunk: 't:heavy-forwarder',
    datadog: 't:log-pipelines',
    elk: 'Logstash',
  },
  {
    featureKey: 'ssoSaml',
    logpulse: 't:oauth-saml-planned',
    splunk: 't:enterprise-only',
    datadog: 't:enterprise-only',
    elk: 't:xpack-paid',
  },
  {
    featureKey: 'retention',
    logpulse: 't:up-to-365-enterprise',
    splunk: 't:license-dependent',
    datadog: 't:datadog-15-default',
    elk: 't:elk-unlimited',
  },
  {
    featureKey: 'setupTime',
    logpulse: 't:setup-5min',
    splunk: 't:setup-days-weeks',
    datadog: 't:setup-hours',
    elk: 't:setup-days-weeks',
  },
  {
    featureKey: 'maintenance',
    logpulse: 't:fully-managed',
    splunk: 't:self-or-cloud',
    datadog: 't:fully-managed',
    elk: 't:self-managed',
  },
  {
    featureKey: 'openStandards',
    logpulse: 't:otlp-native',
    splunk: 't:proprietary',
    datadog: 't:proprietary',
    elk: 't:open-source',
  },
  {
    featureKey: 'publicSlos',
    logpulse: '99.9% (Early Access)',
    splunk: '99.9% (Cloud)',
    datadog: '99.9%',
    elk: 't:na-self-managed',
  },
  {
    featureKey: 'compliance',
    logpulse: 't:compliance-logpulse',
    splunk: 'SOC 2, HIPAA, FedRAMP',
    datadog: 'SOC 2, HIPAA, ISO 27001',
    elk: 't:self-managed',
  },
  {
    featureKey: 'euHosting',
    logpulse: 'GCP Netherlands',
    splunk: 't:us-default',
    datadog: 't:us-default',
    elk: 't:self-managed',
  },
];

/** Sub-table for the value-key snippets used inside cells. The key prefix
 *  is `compare.cell.*` in the i18n bundles. */
export const cellValueKeys = [
  'flat-rate', 'per-gb-indexed', 'per-host-gb', 'free-infra',
  'starting-logpulse',
  'custom', 'built-in', 'add-on-itsi', 'xpack-paid', 'all-plans',
  'datadog-bits-enterprise', 'heavy-forwarder', 'log-pipelines',
  'oauth-saml-planned', 'enterprise-only',
  'up-to-365-enterprise', 'license-dependent', 'datadog-15-default', 'elk-unlimited',
  'setup-5min', 'setup-days-weeks', 'setup-hours',
  'fully-managed', 'self-or-cloud', 'self-managed',
  'otlp-native', 'proprietary', 'open-source',
  'na-self-managed', 'compliance-logpulse', 'us-default',
] as const;

export interface MigrationCard {
  /** i18n key for the platform label, e.g. `compare.migrate.splunk.title` */
  platformKey: string;
  /** Array of i18n keys, e.g. `compare.migrate.splunk.step1` */
  stepKeys: string[];
}

export const migrationCards: MigrationCard[] = [
  {
    platformKey: 'splunk',
    stepKeys: ['step1', 'step2', 'step3', 'step4'],
  },
  {
    platformKey: 'datadog',
    stepKeys: ['step1', 'step2', 'step3', 'step4'],
  },
  {
    platformKey: 'elk',
    stepKeys: ['step1', 'step2', 'step3', 'step4'],
  },
  {
    platformKey: 'other',
    stepKeys: ['step1', 'step2', 'step3', 'step4'],
  },
];

export interface HighlightCardConfig {
  icon: ComponentType<{ className?: string }>;
  /** i18n keys: `compare.highlight.<id>.title` and `.description` */
  id: string;
}

export const highlightCards: HighlightCardConfig[] = [
  { id: 'pricing', icon: DollarSign },
  { id: 'speed', icon: Search },
  { id: 'maintenance', icon: Wrench },
];

export const heroIcon = Zap;
export const ctaIcon = Server;
export const migrateIcon = Clock;

// ───────────────────────────── Per-duo configs ─────────────────────────────

export interface DuoConfig {
  slug: string;            // URL slug e.g. 'datadog-vs-splunk'
  competitors: CompetitorId[]; // 2 (or 3 for the multi-way page)
  /** Highlight icon shown next to the page title. */
  icon: ComponentType<{ className?: string }>;
  /** Sub-set of comparisonRows to show, by featureKey. Empty = all. */
  rowFilter?: string[];
}

export const duos: DuoConfig[] = [
  {
    slug: 'datadog-vs-splunk',
    competitors: ['datadog', 'splunk'],
    icon: Database,
  },
  {
    slug: 'datadog-vs-elk',
    competitors: ['datadog', 'elk'],
    icon: Shield,
  },
  {
    slug: 'splunk-vs-elk',
    competitors: ['splunk', 'elk'],
    icon: Wrench,
  },
  {
    slug: 'datadog-vs-splunk-vs-elk',
    competitors: ['datadog', 'splunk', 'elk'],
    icon: Rocket,
  },
];
